// Microbe RTS — minimap scrub + tap-to-command.
//
// The minimap ribbon renders on an overlay canvas (render.ts) that is pointer-events:none; this
// module owns the thin interactive hit div stacked over it. Two gestures, disambiguated by whether
// the finger travels past a small threshold:
//   • SCRUB (drag)  -> pan the game view: the camera follows the world point under the finger, live.
//   • TAP  (no drag) -> if units are selected, ORDER them to that world point (sim 'move' command);
//                       if nothing is selected, jump the view there instead (a cheap look-around).
//
// It reads the hit element's own live bounds as the source of truth (so it stays correct across
// resize/orientation without threading the ribbon rect through), and talks to the sim ONLY via
// sim.command / sim.state and to the view ONLY through the Camera contract — same discipline as
// ui/input.ts. verbatimModuleSyntax: types imported with `import type`.

import { WORLD, HAPTIC } from '../config.js';
import type { Sim, Vec, Side } from '../types.js';
import type { Camera } from '../render/camera.js';

/** Haptic sink (kit's `haptic`, signature-compatible). */
export type HapticFn = (pattern: number | number[]) => void;

// Finger travel (CSS px) past which a press stops being a tap and becomes a scrub.
const SCRUB_THRESHOLD_PX = 5;

/**
 * Wire scrub + tap-to-command onto the ribbon's hit element.
 * @param hit     the interactive div sized to the ribbon (from mountRender)
 * @param sim     authoritative sim (read selection, issue move orders)
 * @param cam     camera contract (pan the view)
 * @param haptic  kit haptic sink
 */
export function attachMinimapInput(
  hit: HTMLElement,
  sim: Sim,
  cam: Camera,
  haptic: HapticFn,
  viewSide: Side = 'you',
): { destroy(): void } {
  let activeId: number | null = null;
  let moved = false;
  let startX = 0;
  let startY = 0;

  // A screen point (CSS px) -> world point, via the ribbon's live bounds. Fractions are clamped so
  // an edge drag still resolves on-map.
  const worldAt = (clientX: number, clientY: number): Vec => {
    const r = hit.getBoundingClientRect();
    const fx = Math.min(1, Math.max(0, (clientX - r.left) / (r.width || 1)));
    const fy = Math.min(1, Math.max(0, (clientY - r.top) / (r.height || 1)));
    return { x: fx * WORLD.width, y: fy * WORLD.height };
  };

  const onDown = (e: PointerEvent): void => {
    if (activeId !== null) return;
    activeId = e.pointerId;
    moved = false;
    startX = e.clientX;
    startY = e.clientY;
    hit.setPointerCapture?.(e.pointerId);
    e.preventDefault();
    e.stopPropagation();
  };

  const onMove = (e: PointerEvent): void => {
    if (e.pointerId !== activeId) return;
    if (
      Math.abs(e.clientX - startX) > SCRUB_THRESHOLD_PX ||
      Math.abs(e.clientY - startY) > SCRUB_THRESHOLD_PX
    ) {
      moved = true;
      // Past threshold => a scrub: the view snaps directly under the finger (no easing lag) so
      // dragging down the ribbon feels like a true scrub, not a rubber-band.
      cam.jumpTo(worldAt(e.clientX, e.clientY));
    }
    e.preventDefault();
    e.stopPropagation();
  };

  const onUp = (e: PointerEvent): void => {
    if (e.pointerId !== activeId) return;
    activeId = null;
    hit.releasePointerCapture?.(e.pointerId);
    if (!moved) {
      // A clean TAP.
      const world = worldAt(e.clientX, e.clientY);
      if (sim.state.selectionBySide[viewSide].length > 0) {
        sim.command({ t: 'move', pos: world }); // order the selection to the tapped spot
        haptic(HAPTIC.command);
      } else {
        cam.follow(world); // nothing selected -> just look there
        haptic(HAPTIC.command);
      }
    }
    e.preventDefault();
    e.stopPropagation();
  };

  const onCancel = (e: PointerEvent): void => {
    if (e.pointerId !== activeId) return;
    activeId = null;
    moved = false;
    hit.releasePointerCapture?.(e.pointerId);
    e.preventDefault();
    e.stopPropagation();
  };

  hit.addEventListener('pointerdown', onDown);
  hit.addEventListener('pointermove', onMove);
  hit.addEventListener('pointerup', onUp);
  hit.addEventListener('pointercancel', onCancel);

  return {
    destroy(): void {
      hit.removeEventListener('pointerdown', onDown);
      hit.removeEventListener('pointermove', onMove);
      hit.removeEventListener('pointerup', onUp);
      hit.removeEventListener('pointercancel', onCancel);
    },
  };
}
